import { useRef, useState } from 'react'
import { useAuth } from '@/contexts/AuthContext'
import { Field } from '@/pages/vault/VaultForms'
import {
  inspectRecovery,
  reviewRecovery,
  type RecoveryReviewCase,
} from '@/services/vault/accountChanges'
import { validAccountReference, AccountCommandError } from '@/services/vault/accountEnrollment'

export default function AccountRecoveryReviewPage() {
  const { hasPermission } = useAuth()
  const [reference, setReference] = useState(''),
    [record, setRecord] = useState<RecoveryReviewCase | null>(null)
  const [evidence, setEvidence] = useState(''),
    [digest, setDigest] = useState(''),
    [code, setCode] = useState('')
  const [ownership, setOwnership] = useState(false),
    [destination, setDestination] = useState(false),
    [ack, setAck] = useState(false)
  const [decision, setDecision] = useState('dormant'),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false),
    [uncertain, setUncertain] = useState(false)
  const pending = useRef<Record<string, unknown> | null>(null),
    running = useRef(false)
  async function inspect() {
    if (running.current || !validAccountReference(reference)) return
    running.current = true
    setBusy(true)
    setError('')
    try {
      setRecord(await inspectRecovery(reference))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recovery unavailable')
    } finally {
      running.current = false
      setBusy(false)
    }
  }
  async function submit(retry = false) {
    if (running.current || !record) return
    const body = retry
      ? pending.current
      : {
          review_id: crypto.randomUUID(),
          command_id: record.command_id,
          decision,
          evidence_reference: evidence.trim(),
          evidence_sha256: digest.trim(),
          ownership_verified: ownership,
          destination_verified: destination,
          acknowledge_no_document_disclosure: ack,
          mfa_code: code.trim(),
        }
    if (!body) return
    pending.current = body
    running.current = true
    setBusy(true)
    setError('')
    try {
      await reviewRecovery(body)
      pending.current = null
      setUncertain(false)
      setCode('')
      setRecord(await inspectRecovery(record.command_id))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Review unavailable')
      const unknown = e instanceof AccountCommandError && e.uncertain
      setUncertain(unknown)
      if (!unknown) pending.current = null
    } finally {
      running.current = false
      setBusy(false)
    }
  }
  if (!hasPermission('txh.platform.user.update'))
    return <p>Platform account review permission is required.</p>
  return (
    <main className="max-w-2xl space-y-5 text-text-primary">
      <h1 className="text-2xl font-semibold">Manual account recovery review</h1>
      <p>
        Verify ownership independently using the manual verification procedure. Email possession
        alone is insufficient. Never open or disclose account documents to establish ownership. Two
        distinct current platform administrators must approve.
      </p>
      <form
        className="card p-6 space-y-3"
        onSubmit={e => {
          e.preventDefault()
          void inspect()
        }}
      >
        <Field
          label="Recovery request ID"
          required
          value={reference}
          onChange={e => {
            setReference(e.target.value)
            setRecord(null)
          }}
        />
        <button className="btn btn-secondary" disabled={busy || !validAccountReference(reference)}>
          Inspect request
        </button>
      </form>
      {record && (
        <section className="card p-6 space-y-4">
          <p>Status: {record.status.replace(/_/g, ' ')}</p>
          <p className="break-all">Account ID: {record.account_id || 'Unavailable'}</p>
          <p>
            Verified destination:{' '}
            {record.destination_email_verified ? record.destination_email : 'Not verified'}
          </p>
          <p>Expires: {record.expires_at}</p>
          {record.reviews.map(r => (
            <p className="text-sm break-all" key={r.reviewer_id}>
              {r.reviewer_id}: {r.decision} · {r.evidence_reference}
            </p>
          ))}
          {['awaiting_review', 'approved'].includes(record.status) && record.reviews.length < 2 && (
            <form
              className="space-y-3"
              onSubmit={e => {
                e.preventDefault()
                void submit()
              }}
            >
              <label className="block">
                Decision
                <select
                  className="input input-bordered w-full"
                  value={decision}
                  onChange={e => setDecision(e.target.value)}
                >
                  <option value="dormant">Insufficient evidence: keep account inaccessible</option>
                  <option value="approve">Ownership verified: approve recovery</option>
                </select>
              </label>
              <Field
                label="Sealed verification evidence reference"
                required
                value={evidence}
                onChange={e => setEvidence(e.target.value)}
              />
              <Field
                label="Evidence SHA-256"
                pattern="[a-f0-9]{64}"
                required
                value={digest}
                onChange={e => setDigest(e.target.value)}
              />
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={ownership}
                  onChange={e => setOwnership(e.target.checked)}
                />
                I independently verified account ownership beyond email possession.
              </label>
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  checked={destination}
                  onChange={e => setDestination(e.target.checked)}
                />
                I verified that this destination belongs to the proven account owner.
              </label>
              <label className="flex gap-2">
                <input
                  type="checkbox"
                  required
                  checked={ack}
                  onChange={e => setAck(e.target.checked)}
                />
                I disclosed no account documents during this verification.
              </label>
              <Field
                label="Your current authenticator or backup code"
                required
                value={code}
                onChange={e => setCode(e.target.value)}
              />
              <button
                className="btn btn-primary"
                disabled={
                  busy ||
                  uncertain ||
                  !ack ||
                  (decision === 'approve' && (!ownership || !destination || !record.account_id))
                }
              >
                Record review
              </button>
            </form>
          )}
          {uncertain && pending.current && (
            <button className="btn btn-secondary" disabled={busy} onClick={() => void submit(true)}>
              Retry exact original review
            </button>
          )}
        </section>
      )}
      {error && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}
    </main>
  )
}
